import React, { useEffect, useState } from "react";
import {
  ClipboardList,
  Brain,
  User,
  LogOut,
  Home,
  CalendarCheck,
  Shield,
  Users
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";

export default function AdminSidebar() {
  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;
  
  // State for current selection
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  
  const location = useLocation();

  // --- Data Fetching ---
  useEffect(() => {
    const fetchUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error) setUserId(data.user.id);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (!userId) return;
    const fetchRole = async () => {
      const { data, error } = await supabase
        .from("users")
        .select("role")
        .eq("id", userId)
        .single();
      if (!error && data) setRole(data.role);
    };
    fetchRole();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const fetchPrograms = async () => {
      const { data, error } = await supabase
        .from("programs")
        .select("*, teams(*)")
        .eq("administrator", userId);

      if (!error && data?.length) {
        // Initialize selection (default to first program/first team)
        const initialProgram = data[0];
        setSelectedProgram(initialProgram);
        setSelectedTeam(initialProgram.teams?.[0] || null);
      }
    };
    fetchPrograms();
  }, [userId]);

  // --- Handlers ---

  const isActive = (path) => location.pathname === path;
  const isSwotRoute = location.pathname.startsWith("/skills-trainer/swot");

  // --- UPDATED: Get Link Logic ---
  const getLink = (type) => {
    // 1. Handle Dashboard separately
    if (type === 'dashboard') {
      if (role === "SkillsTrainer") {
        return "/skills-trainer";
      }
      if (selectedProgram && selectedTeam) {
        return `/admin/program/${selectedProgram.id}/team/${selectedTeam.id}/dashboard`;
      }
      return '/admin'; 
    }

    // 2. Program Level Links (Require only Program)
    if (type === 'audience') {
        if (selectedProgram) {
            return `/admin/program/${selectedProgram.id}/followed-users`;
        }
        return "#";
    }

    if (type === 'profile') {
      if (role === "SkillsTrainer") return "/skills-trainer/profile";
      if (selectedProgram && selectedTeam) {
        return `/admin/program/${selectedProgram.id}/team/${selectedTeam.id}/profile`;
      }
      return "/admin";
    }

    // 3. Team Level Links (Require Program & Team)
    if (!selectedProgram || !selectedTeam) return "#";

    const base = `/admin/program/${selectedProgram.id}/team/${selectedTeam.id}`;

    switch (type) {
      case 'tryouts': return `${base}/tryouts`;
      case 'combine': return `${base}/combine-scores`;
      default: return "#";
    }
  };

  // --- 1. DESKTOP SIDEBAR ---
  const DesktopSidebar = () => (
    <aside
      className={`hidden md:flex flex-col bg-gray-900 text-gray-100 h-screen overflow-hidden transition-all duration-300 border-r border-gray-800 ${
        collapsed ? "w-16" : "w-64"
      }`}
      onMouseEnter={() => setCollapsed(false)}
      onMouseLeave={() => setCollapsed(true)}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex flex-col items-center">
          <a href="/home" className="w-full text-center flex flex-col items-center gap-2">
            <img
              src={brandLogo}
              alt="Nuvio"
              className="w-8 h-8 rounded-lg object-cover shadow-sm"
            />
            <h1
              className={`text-xl font-bold transition-all duration-300 whitespace-nowrap ${
                collapsed ? "opacity-0 w-0 mx-auto overflow-hidden" : "opacity-100"
              }`}
            >
              Nuvio Sport
            </h1>
          </a>
          {!collapsed && (
            <div className="mt-2 text-[10px] uppercase tracking-wider text-gray-500">
              {role === "SkillsTrainer" ? "Skills Trainer" : "Program Admin"}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-2 overflow-y-auto">
          
          {/* DASHBOARD */}
          <Link
            to={getLink('dashboard')}
            title={collapsed ? "Dashboard" : ""}
            className={`flex items-center gap-3 px-3 py-2 rounded transition ${
                isActive(getLink('dashboard')) ? "bg-gray-800 text-white" : "text-gray-300 hover:bg-gray-800"
              }`}
          >
            <ClipboardList size={18} />
            {!collapsed && <span>Dashboard</span>}
          </Link>

          {selectedProgram && (
            <Link
              to={getLink('audience')}
              title={collapsed ? (role === "SkillsTrainer" ? "Clients" : "Prospective Athletes") : ""}
              className={`flex items-center gap-3 px-3 py-2 rounded transition ${
                  isActive(getLink('audience')) ? "bg-gray-800 text-white" : "text-gray-300 hover:bg-gray-800"
                }`}
            >
              <Users size={18} />
              {!collapsed && (
              <span>
                {role === "SkillsTrainer" ? "Clients" : "Prospective Athletes"}
              </span>
              )}
            </Link>
          )}

          {/* TEAM SPECIFIC LINKS */}
          {selectedProgram && selectedTeam && (
            <>
              <Link
                to={getLink('combine')}
                title={collapsed ? "Combine Scores" : ""}
                className={`flex items-center gap-3 px-3 py-2 rounded transition ${
                    isActive(getLink('combine')) ? "bg-gray-800 text-white" : "text-gray-300 hover:bg-gray-800"
                  }`}
              >
                <Brain size={18} />
                {!collapsed && <span>Combine Scores</span>}
              </Link>

              <Link
                to={getLink('tryouts')}
                title={collapsed ? "Tryouts" : ""}
                className={`flex items-center gap-3 px-3 py-2 rounded transition ${
                    isActive(getLink('tryouts')) ? "bg-gray-800 text-white" : "text-gray-300 hover:bg-gray-800"
                  }`}
              >
                <CalendarCheck size={18} />
                {!collapsed && <span>Tryouts</span>}
              </Link>
            </>
          )}

          <div className="mt-4 space-y-2">
            <Link
              to={getLink('profile')}
              title={collapsed ? "Profile" : ""}
              className={`flex items-center gap-3 px-3 py-2 rounded transition ${
                    isActive(getLink('profile')) ? "bg-gray-800 text-white" : "text-gray-300 hover:bg-gray-800"
                  }`}
              >
                <User size={18} />
                {!collapsed && <span>Profile</span>}
              </Link>
          </div>

          <div className="pt-4 mt-auto border-t border-gray-800">
            <Link
              to="/logout"
              className="flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-800 transition text-gray-300"
              title={collapsed ? "Logout" : ""}
            >
              <LogOut size={18} />
              {!collapsed && <span>Logout</span>}
            </Link>
          </div>
        </nav>
      </div>
    </aside>
  );

  // --- 3. MOBILE BOTTOM NAV (SAFE AREA FIXED) ---
  const MobileNavItem = ({ to, icon: Icon, label }) => {
    const active = to === "/skills-trainer/swot"
      ? isSwotRoute
      : location.pathname === to;
    const disabled = to === "#";

    return (
      <Link
        to={to}
        onClick={e => disabled && e.preventDefault()}
        className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${
          active 
            ? "text-blue-500" 
            : disabled 
              ? "text-gray-700 cursor-not-allowed" 
              : "text-gray-500 hover:text-gray-300"
        }`}
      >
        <Icon size={22} strokeWidth={active ? 2.5 : 2} />
        <span className="text-[9px] font-medium">{label}</span>
      </Link>
    );
  };

  const MobileBottomNav = () => (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-xl border-t border-gray-800 pt-2 pb-0 transition-all duration-300">
      <div className="grid grid-cols-5 h-16 items-center px-1">

        <MobileNavItem
          to={getLink('tryouts')}
          icon={CalendarCheck}
          label="Tryouts"
        />

        <MobileNavItem
          to={getLink('combine')}
          icon={Brain}
          label="Combine"
        />

        {/* Dashboard (Center Floating) */}
        <div className="relative -top-5 flex justify-center">
          <Link
            to={getLink('dashboard')}
            className={`flex items-center justify-center w-14 h-14 rounded-full shadow-lg border-4 border-gray-900 transition-all active:scale-95 ${isActive(getLink('dashboard'))
                ? "bg-blue-600 text-white shadow-blue-900/40"
                : "bg-gray-800 text-gray-400"
              }`}
          >
            <Home size={24} />
          </Link>
        </div>

        <MobileNavItem
          to={getLink('profile')}
          icon={User}
          label="Profile"
        />

        <MobileNavItem
          to="/logout"
          icon={LogOut}
          label="Logout"
        />

      </div>
    </div>
  );

  return (
    <>
      <DesktopSidebar />
      <MobileBottomNav />
    </>
  );
}
